import { create } from 'zustand';

/**
 * avatarEffectStore (2026-08-20) — 내 프로필 사진 둘레에 넣는 효과.
 *
 * **내 화면에서만** 보인다(2026-08-20 사용자 확정). 남에게도 보이려면 서버에 값을 저장해야
 * 하는데, 그건 컬럼 추가가 필요해 2차로 미뤘다. 그래서 지금은 기기별 저장(localStorage)이다.
 *
 * 효과는 CSS 만으로 그린다 — 이미지도 라이브러리도 더 받지 않는다. 움직이는 효과는
 * `prefers-reduced-motion` 전역 규칙이 자동으로 멈춘다(tokens.css).
 */
export type AvatarEffect = 'none' | 'status' | 'ring' | 'spin' | 'pulse' | 'glow' | 'frame';

export const AVATAR_EFFECTS: readonly AvatarEffect[] = [
  'none', 'status', 'ring', 'spin', 'pulse', 'glow', 'frame',
];

/** 확정 기본값 — 상태 링(접속 상태를 점으로). */
export const DEFAULT_AVATAR_EFFECT: AvatarEffect = 'status';

const KEY = 'axi.profile.avatarEffect';
const isBrowser = typeof window !== 'undefined';

function read(): AvatarEffect {
  if (!isBrowser) return DEFAULT_AVATAR_EFFECT;
  try {
    const v = window.localStorage.getItem(KEY);
    return AVATAR_EFFECTS.includes(v as AvatarEffect) ? (v as AvatarEffect) : DEFAULT_AVATAR_EFFECT;
  } catch {
    return DEFAULT_AVATAR_EFFECT;
  }
}

interface AvatarEffectState {
  effect: AvatarEffect;
  setEffect: (effect: AvatarEffect) => void;
  hydrate: () => void;
}

export const useAvatarEffectStore = create<AvatarEffectState>((set) => ({
  effect: read(),
  setEffect: (effect) => {
    if (isBrowser) {
      try { window.localStorage.setItem(KEY, effect); } catch { /* ignore */ }
    }
    set({ effect });
  },
  hydrate: () => set({ effect: read() }),
}));

/** 테스트 전용 — 저장값·상태 초기화. */
export function _resetAvatarEffectForTests(): void {
  if (isBrowser) {
    try { window.localStorage.removeItem(KEY); } catch { /* ignore */ }
  }
  useAvatarEffectStore.setState({ effect: DEFAULT_AVATAR_EFFECT });
}
